import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { deleteBd, getTableByIdBd, insertTableBd, updateTableBd } from '../../config/functions';
import { crearPlantillaCompletaDefault } from '../../config/functionsPlantillasDefault';
import ModalVariableConfigOpt from './ModalVariableConfigOpt';
import ModalPlantillaOpt from './ModalPlantillaOpt';
import NotificationFooter, { showNotification } from '../utils/NotificationFooter';
import ConfirmDialog, { showConfirmDialog } from '../utils/ConfirmDialog';
import '../../css/liveDataOpt.css';
import '../../css/modalTheme.css';

// 🎨 TEMA OSCURO - Colores actuales del sistema (perfectos)
const THEME_DARK = {
    bgColor: '#000000',
    fontColor: '#ffffff',
    cardBg: '#0a0a0a',
    cardBorder: '#333333',
    gridColor: '#444444',
    measureValueColor: '#45B7D1',
    alarmColor: '#ff6b6b'
};

// ðŸŽ¨ TEMA CLARO - Colores para fondo claro
const THEME_LIGHT = {
    bgColor: '#ffffff',
    fontColor: '#212529',
    cardBg: '#f8f9fa',
    cardBorder: '#dee2e6',
    gridColor: '#e0e0e0',
    measureValueColor: '#1a5490',
    alarmColor: '#dc3545'
};

// ðŸ”’ SINGLETON - Controlar que solo haya UNA instancia del polling activa
let activePollingInstance = null;
let activeTimers = []; // Array de todos los timers activos para cancelarlos

const LiveDataOpt = ({
    rig,
    setRig,
    mobile,
    variables,
    uid,
    user,
    colors,
    setComponente,
    componente,
    horizontal,

    enVivo, setEnVivo,
    
    // Tab props (solo desktop)
    openTabs, setOpenTabs, activeTabId, setActiveTabId, showRigSelector, setShowRigSelector,
    // Cache props
    getCachedRigData, setCachedRigData, updateCachedRigData, clearCachedRigData,
    // Lista de rigs
    rigs
}) => {

    // â±ï¸ CONFIGURACIÃ“N - Sistema de actualizaciÃ³n inteligente
    const POLLING_INTERVAL = 10000; // 10 segundos - consulta a BD
    const DISPLAY_INTERVAL = 2000; // 2 segundos - actualizaciÃ³n visual
    const MAX_RETRIES = 3;

    // ðŸ“Š ESTADOS DE DATOS
    const [liveDataM, setLiveDataM] = useState([]);
    const [lastUpdateDate, setLastUpdateDate] = useState(null);
    const [sinDatos, setSinDatos] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // ðŸŽ¨ ESTADOS DE VISTA
    const [tipoVista, setTipoVista] = useState(sessionStorage.getItem('tipoVista') || 'live');
    const [forceRenderKey, setForceRenderKey] = useState(0);
    const [themeMode, setThemeMode] = useState(() => localStorage.getItem('theme') || 'dark');

    // ðŸŽ¨ Selector de tema estable (sin Date.now() ni valores dinÃ¡micos)
    const theme = useMemo(() => {
        if (themeMode === 'light') {
            return THEME_LIGHT;
        } else if (themeMode === 'custom' && colors) {
            // Para tema custom, mapear colores del objeto colors a la estructura theme
            return {
                bgColor: colors.ldColorBg || colors.colorBg || '#000000',
                fontColor: colors.ldColorFont || colors.colorFont || '#ffffff',
                cardBg: colors.ldColorBg || '#0a0a0a',
                cardBorder: colors.ldColorBorder || '#333333',
                gridColor: colors.grColorBorder || '#444444',
                measureValueColor: colors.ldColorMeasure || '#45B7D1',
                alarmColor: colors.ldColorAlarm || '#ff6b6b'
            };
        }
        // Por defecto siempre dark (incluye 'dark' y cualquier otro valor)
        return THEME_DARK;
    }, [themeMode, colors]);

    // ðŸš¨ ESTADOS DE ALARMA
    const [currentAlarm, setCurrentAlarm] = useState({
        varName: '',
        varId: '',
        min: null,
        max: null,
        exists: false,
        id: null
    });

    // ðŸ“ ESTADOS DE MODAL
    const [showModalConfig, setShowModalConfig] = useState(false);
    const [modalInitialTab, setModalInitialTab] = useState('variable');
    const [showModalPlantilla, setShowModalPlantilla] = useState(false);
    const [updateVarMatrizId, setUpdateVarMatrizId] = useState(0);

    // ðŸŽ¯ ESTADOS DE DRAG AND DROP
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    // ðŸ“± DETECCIÃ“N DE ORIENTACIÃ“N INTERNA
    const [internalOrientation, setInternalOrientation] = useState({
        isMobile: mobile,
        isHorizontal: horizontal
    });

    // ðŸ“± Determinar modo de vista mÃ³vil
    const isMobileLandscape = internalOrientation.isMobile && internalOrientation.isHorizontal;
    const isMobilePortrait = internalOrientation.isMobile && !internalOrientation.isHorizontal;

    // ðŸ”„ REFERENCIAS PARA PERFORMANCE
    const instanceIdRef = useRef(Math.random().toString(36).substr(2, 9)); // ID Ãºnico de instancia
    const intervalRef = useRef(null);
    const lastFetchDateRef = useRef(null);
    const rigRef = useRef(rig);
    const retryCountRef = useRef(0);
    const isPollingActiveRef = useRef(false); // Bandera para evitar polling duplicado
    const isFetchingRef = useRef(false);
    const nbRef = useRef(null); // Ãšltimo timestamp conocido
    const enVivoRef = useRef(false); // Estado de transmisiÃ³n (ref para evitar re-renders)
    const lastNewDataTimeRef = useRef(Date.now()); // Ãšltima vez que hubo datos nuevos
    const pendingUpdatesRef = useRef([]); // Cola de actualizaciones pendientes
    const displayTimerRef = useRef(null); // Timer para actualizaciÃ³n visual gradual

    // ðŸŽ¯ ACTUALIZAR REFS
    useEffect(() => {
        rigRef.current = rig;
    }, [rig]);

    // ðŸŽ¨ DETECTAR CAMBIOS DE TEMA DESDE LOCALSTORAGE
    useEffect(() => {
        const handleThemeChange = () => {
            const newTheme = localStorage.getItem('theme') || 'dark';
            if (newTheme !== themeMode) {
                setThemeMode(newTheme);
                setForceRenderKey(prev => prev + 1);
            }
            // Aplicar atributo data-theme al documento para CSS (SIEMPRE, aunque no haya cambiado)
            document.documentElement.setAttribute('data-theme', newTheme);
        };

        // Aplicar tema inicial
        handleThemeChange();

        // Escuchar evento personalizado para cambios de tema en la misma pestaÃ±a
        window.addEventListener('themeChanged', handleThemeChange);
        
        // Storage event solo funciona entre pestaÃ±as, pero lo agregamos por si acaso
        window.addEventListener('storage', (e) => {
            if (e.key === 'theme') {
                handleThemeChange();
            }
        });

        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
            window.removeEventListener('storage', handleThemeChange);
        };
    }, [themeMode]);

    // ðŸ“± DETECTAR CAMBIOS DE ORIENTACIÃ“N
    useEffect(() => {
        let resizeTimeout;
        
        const detectOrientation = () => {
            // Limpiar timeout anterior si existe
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            // Esperar a que la rotaciÃ³n se complete antes de detectar
            resizeTimeout = setTimeout(() => {
                const width = window.innerWidth;
                const height = window.innerHeight;
                
                // Detectar dispositivo mÃ³vil usando mÃºltiples mÃ©todos
                const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                
                // Si tiene userAgent mÃ³vil Y tiene capacidad tÃ¡ctil, ES mÃ³vil (sin importar el tamaÃ±o de pantalla)
                const isMobileDevice = isMobileUserAgent && isTouchDevice;
                
                const isHorizontalOrientation = width > height;

                const newOrientation = {
                    isMobile: isMobileDevice,
                    isHorizontal: isHorizontalOrientation
                };

                setInternalOrientation(prev => {
                    if (prev.isMobile !== newOrientation.isMobile || prev.isHorizontal !== newOrientation.isHorizontal) {
                        /* console.log('ðŸ“± LiveDataOpt - Cambio de orientaciÃ³n:', {
                            ...newOrientation,
                            width,
                            height,
                            userAgent: isMobileUserAgent,
                            touch: isTouchDevice,
                            maxTouchPoints: navigator.maxTouchPoints,
                            hasOntouchstart: 'ontouchstart' in window
                        }); */
                        setForceRenderKey(k => k + 1);
                        return newOrientation;
                    }
                    return prev;
                });
            }, 150); // Debounce de 150ms
        };

        detectOrientation();
        window.addEventListener('resize', detectOrientation);
        window.addEventListener('orientationchange', detectOrientation);

        return () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            window.removeEventListener('resize', detectOrientation);
            window.removeEventListener('orientationchange', detectOrientation);
        };
    }, []);

    // ðŸ’¾ GUARDAR TIPO DE VISTA
    useEffect(() => {
        sessionStorage.setItem('tipoVista', tipoVista);
    }, [tipoVista]);

    // ðŸŽ¨ ACTUALIZAR CSS CUSTOM PROPERTIES
    useEffect(() => {
        document.documentElement.style.setProperty('--color-default', colors.ldColorFont || '#ffffff');
        document.documentElement.style.setProperty('--color-measure', colors.ldColorMeasure || '#4ECDC4');
        document.documentElement.style.setProperty('--color-alarm', colors.ldColorAlarm || '#ff6b6b');
        
        // Actualizar color de unidad segÃºn el tema
        const unitColor = themeMode === 'light' 
            ? 'rgba(33, 37, 41, 0.6)' // Gris oscuro semi-transparente para tema claro
            : 'rgba(255, 255, 255, 0.7)'; // Blanco semi-transparente para tema oscuro
        document.documentElement.style.setProperty('--ldopt-color-unit', unitColor);
    }, [colors, themeMode]);

    // ðŸŽ¯ AGREGAR ACTUALIZACIONES A LA COLA Y PROCESARLAS GRADUALMENTE
    const addDataToQueue = useCallback((dataArray, totalDataPoints) => {
        if (!dataArray || dataArray.length === 0) return;

        // Agrupar datos por timestamp
        const dataByTimestamp = new Map();
        dataArray.forEach(record => {
            const timestamp = new Date(record.dt_mda_fch).getTime();
            if (!dataByTimestamp.has(timestamp)) {
                dataByTimestamp.set(timestamp, []);
            }
            dataByTimestamp.get(timestamp).push(record);
        });

        // Ordenar timestamps cronolÃ³gicamente (mÃ¡s antiguo primero)
        const timestamps = Array.from(dataByTimestamp.keys()).sort((a, b) => a - b);

        // Limpiar cola anterior y agregar nuevas actualizaciones
        pendingUpdatesRef.current = [];

        timestamps.forEach(timestamp => {
            pendingUpdatesRef.current.push({
                timestamp,
                data: dataByTimestamp.get(timestamp)
            });
        });

        // Calcular intervalo dinÃ¡mico basado en cantidad de timestamps Ãºnicos
        // Si hay N timestamps Ãºnicos, distribuirlos en 10 segundos
        const numUpdates = timestamps.length;
        const dynamicInterval = numUpdates > 0 ? POLLING_INTERVAL / numUpdates : POLLING_INTERVAL;

        //console.log(`ðŸ“Š LiveDataOpt - ${numUpdates} actualizaciones en cola, intervalo: ${dynamicInterval}ms`);

        // Si no hay timer activo, iniciar procesamiento con intervalo dinÃ¡mico
        if (!displayTimerRef.current) {
            processNextUpdate(dynamicInterval);
        }
    }, []);

    // ðŸ”„ PROCESAR SIGUIENTE ACTUALIZACIÃ“N DE LA COLA
    const processNextUpdate = useCallback((interval) => {
        if (pendingUpdatesRef.current.length === 0) {
            displayTimerRef.current = null;
            //console.log('âœ… LiveDataOpt - Cola de actualizaciones vacÃ­a');
            return;
        }

        const update = pendingUpdatesRef.current.shift();
        const remainingUpdates = pendingUpdatesRef.current.length;

        //console.log(`ðŸ”„ LiveDataOpt - Procesando update, ${remainingUpdates} restantes en cola`);

        // ðŸ”‘ IMPORTANTE: Fusionar datos nuevos con estructura existente
        // NO reemplazar toda la grilla, solo actualizar valores que cambiaron
        setLiveDataM(prevData => {
            // Si no hay datos previos, usar los nuevos directamente
            if (!prevData || prevData.length === 0) {
                return update.data.sort((a, b) => a.nb_matr_colum - b.nb_matr_colum);
            }

            // Crear copia de datos existentes
            const updatedData = [...prevData];

            // Actualizar solo las variables que tienen nuevos valores
            update.data.forEach(newRecord => {
                const index = updatedData.findIndex(
                    item => item.nb_var_idn === newRecord.nb_var_idn
                );

                if (index !== -1) {
                    // Actualizar solo los valores, mantener el resto de propiedades
                    updatedData[index] = {
                        ...updatedData[index], // Mantener estructura original
                        nb_mda_val: newRecord.nb_mda_val, // Actualizar valor
                        dt_mda_fch: newRecord.dt_mda_fch  // Actualizar fecha
                    };
                }
            });

            return updatedData;
        });

        setLastUpdateDate(new Date(update.timestamp).toISOString());

        // Actualizar refs
        nbRef.current = update.timestamp;
        lastFetchDateRef.current = new Date(update.timestamp).toISOString();

        // Programar siguiente actualizaciÃ³n con el mismo intervalo
        if (remainingUpdates > 0) {
            displayTimerRef.current = setTimeout(() => processNextUpdate(interval), interval);
        } else {
            displayTimerRef.current = null;
        }
    }, []);

    // ðŸ”„ FETCH OPTIMIZADO DE DATOS - Sistema inteligente con polling
    const fetchLiveData = useCallback(async () => {
        // Evitar llamadas simultÃ¡neas
        if (isFetchingRef.current) {
            //console.log('âš ï¸ LiveDataOpt - Fetch ya en progreso, cancelando duplicado');
            return false;
        }

        if (!rigRef.current || rigRef.current === 0 || rigRef.current === '') {
            //console.log('âš ï¸ LiveDataOpt - Sin rig seleccionado');
            return false;
        }

        //console.log(`ðŸ“¡ LiveDataOpt - Ejecutando fetchLiveData para rig ${rigRef.current}`);
        isFetchingRef.current = true;

        try {
            // Primera consulta - obtener configuraciÃ³n de variables
            if (!nbRef.current) {
                //console.log('ðŸ“‹ LiveDataOpt - Primera consulta: obteniendo configuraciÃ³n inicial');
                const dataSend = `${rigRef.current}:${uid}`;
                let configData = await getTableByIdBd(dataSend, 'getvarsalarmmatr');
                //console.log(`ðŸ“‹ LiveDataOpt - ConfiguraciÃ³n obtenida: ${configData.length} variables`);

                // Eliminar duplicados por columna
                configData = configData.filter((thing, index, self) =>
                    index === self.findIndex((t) => t.nb_matr_colum === thing.nb_matr_colum)
                );

                // Verificar si hay plantilla configurada
                if (configData.length === 0) {
                    //console.log('ðŸ“ LiveDataOpt - No hay plantilla, creando plantilla por defecto...');
                    setSinDatos(true);
                    
                    // Crear plantilla por defecto
                    await crearPlantillaCompletaDefault('liveData', rigRef.current, user, uid, null, null);
                    
                    // Esperar un momento para que se inserte en BD
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // Reintentar consulta despuÃ©s de crear plantilla
                    //console.log('ðŸ”„ LiveDataOpt - Reintentando consulta de plantilla...');
                    const dataSend2 = `${rigRef.current}:${uid}`;
                    configData = await getTableByIdBd(dataSend2, 'getvarsalarmmatr');
                    configData = configData.filter((thing, index, self) =>
                        index === self.findIndex((t) => t.nb_matr_colum === thing.nb_matr_colum)
                    );
                    
                    if (configData.length === 0) {
                        console.warn('âš ï¸ LiveDataOpt - No se pudo obtener la plantilla despuÃ©s de crearla');
                        isFetchingRef.current = false;
                        return false;
                    }
                    //console.log('âœ… LiveDataOpt - Plantilla obtenida correctamente despuÃ©s de crearla');
                }
                
                setSinDatos(false);

                // Obtener Ãºltimo dato para cada variable
                const valorArr = configData.find(r => r.dt_mda_fch !== null);

                if (valorArr?.dt_mda_fch) {
                    const nuevaFecha = new Date(valorArr.dt_mda_fch);
                    nbRef.current = nuevaFecha.getTime();
                    lastFetchDateRef.current = valorArr.dt_mda_fch;

                    // Actualizar UI
                    configData.sort((a, b) => a.nb_matr_colum - b.nb_matr_colum);
                    setLiveDataM(configData);
                    setLastUpdateDate(valorArr.dt_mda_fch);

                    enVivoRef.current = true;
                    setEnVivo(true);

                    retryCountRef.current = 0;
                    isFetchingRef.current = false;
                    return true;
                } else {
                    // Sin datos de fecha
                    configData.sort((a, b) => a.nb_matr_colum - b.nb_matr_colum);
                    setLiveDataM(configData);
                    setLastUpdateDate(null);
                    isFetchingRef.current = false;
                    return false;
                }
            } else {
                // Consultas siguientes - obtener datos desde Ãºltimo timestamp hasta ahora
                const fechaF = new Date().toISOString().slice(0, 19); // Ahora
                const fechaI = new Date(nbRef.current + 1).toISOString().slice(0, 19); // Ãšltimo timestamp + 1ms

                //console.log(`ðŸ“… LiveDataOpt - Consultando datos entre ${fechaI} y ${fechaF}`);

                // Obtener configuraciÃ³n actual desde la BD (no del estado)
                const dataSend = `${rigRef.current}:${uid}`;
                const currentConfig = await getTableByIdBd(dataSend, 'getvarsalarmmatr');
                //console.log(`ðŸ“‹ LiveDataOpt - ConfiguraciÃ³n actual: ${currentConfig.length} variables`);

                if (!currentConfig || currentConfig.length === 0) {
                    isFetchingRef.current = false;
                    return false;
                }

                const varsIds = currentConfig.map(item => item.nb_var_idn).join(',');
                const dataQuerySend = `${rigRef.current};${varsIds};${fechaF};${fechaI}`;

                //console.log(`ðŸ” LiveDataOpt - Consultando getvarstldrofecha con ${currentConfig.length} variables`);
                const newData = await getTableByIdBd(dataQuerySend, 'getvarstldrofecha');
                //console.log(`ðŸ“¦ LiveDataOpt - Respuesta BD: ${newData ? newData.length : 0} registros`);

                if (newData && newData.length > 0) {
                    // Filtrar solo datos NUEVOS (timestamp > Ãºltimo conocido)
                    //console.log(`ðŸ” LiveDataOpt - Ãšltimo timestamp conocido: ${nbRef.current ? new Date(nbRef.current).toISOString() : 'null'}`);

                    const filteredData = newData.filter(record => {
                        const recordTimestamp = new Date(record.dt_mda_fch).getTime();
                        return recordTimestamp > nbRef.current;
                    });

                    //console.log(`ðŸ” LiveDataOpt - DespuÃ©s del filtro: ${filteredData.length} registros nuevos (de ${newData.length} recibidos)`);

                    if (filteredData.length === 0) {
                        // No hay datos nuevos - taladro en standby
                        //console.log(`â¸ï¸ LiveDataOpt - Sin datos nuevos, taladro en standby`);
                        enVivoRef.current = false;
                        setEnVivo(false);
                        isFetchingRef.current = false;
                        return false;
                    }

                    // Combinar con configuraciÃ³n de alarmas
                    const enrichedData = filteredData.map(record => {
                        const configItem = currentConfig.find(c => c.nb_var_idn === record.nb_var_idn);
                        return {
                            ...record,
                            nb_alarm_min: configItem?.nb_alarm_min || null,
                            nb_alarm_max: configItem?.nb_alarm_max || null,
                            nb_alarm_idn: configItem?.nb_alarm_idn || null,
                            nb_matr_idn: configItem?.nb_matr_idn || null,
                            nb_matr_colum: configItem?.nb_matr_colum || 0
                        };
                    });

                    //console.log(`ðŸ“¥ LiveDataOpt - Recibidos ${filteredData.length} registros nuevos`);

                    // Agregar a la cola para procesamiento gradual
                    addDataToQueue(enrichedData, filteredData.length);

                    enVivoRef.current = true;
                    setEnVivo(true);
                    retryCountRef.current = 0;

                    isFetchingRef.current = false;
                    return true;
                }

                // No hay datos - taladro en standby
                enVivoRef.current = false;
                setEnVivo(false);
                isFetchingRef.current = false;
                return false;
            }

        } catch (error) {
            console.error('âŒ Error fetching live data:', error);
            retryCountRef.current++;

            if (retryCountRef.current >= MAX_RETRIES) {
                // Usar toast aquÃ­ porque showNotification aÃºn no estÃ¡ definido
                toast.error('Error al obtener datos. Reintentando...');
                retryCountRef.current = 0;
            }

            isFetchingRef.current = false;
            throw error;
        }
    }, [uid, user, addDataToQueue]); // REMOVIDO liveDataM para evitar recreaciÃ³n constante

    // ðŸš€ POLLING INTELIGENTE - Consulta cada 10 segundos, actualiza UI cada 2 segundos
    const startSmartPolling = useCallback(() => {
        const myInstanceId = instanceIdRef.current;

        // Cancelar TODOS los timers existentes de cualquier instancia
        //console.log(`ðŸ›‘ [${myInstanceId}] LiveDataOpt - Cancelando ${activeTimers.length} timers activos`);
        activeTimers.forEach(timer => clearTimeout(timer));
        activeTimers = [];

        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
        }

        // Registrar esta instancia como la activa
        activePollingInstance = myInstanceId;
        isPollingActiveRef.current = true;
        let lastPollTime = Date.now();

        const smartPoll = async () => {
            const now = Date.now();
            const timeSinceLastPoll = now - lastPollTime;
            //console.log(`â° [${instanceIdRef.current}] LiveDataOpt - Tiempo desde Ãºltima consulta BD: ${timeSinceLastPoll}ms (esperado: ${POLLING_INTERVAL}ms)`);
            lastPollTime = now;

            try {
                //console.log(`ðŸ”µ [${instanceIdRef.current}] LiveDataOpt - Iniciando consulta a BD...`);
                const startTime = Date.now();

                const hadNewData = await fetchLiveData();

                const queryTime = Date.now() - startTime;
                //console.log(`âœ… [${instanceIdRef.current}] LiveDataOpt - Consulta BD completada en ${queryTime}ms. Nuevos datos: ${hadNewData}`);

                if (hadNewData) {
                    retryCountRef.current = 0; // Reset errores
                }

                // Programar siguiente consulta en 10 segundos
                //console.log(`â²ï¸ [${instanceIdRef.current}] LiveDataOpt - Programando prÃ³xima consulta BD en ${POLLING_INTERVAL}ms (10 segundos)`);
                const timerId = setTimeout(smartPoll, POLLING_INTERVAL);
                intervalRef.current = timerId;
                activeTimers.push(timerId); // Registrar en array global

            } catch (error) {
                console.error('âŒ Error en polling:', error);

                // Backoff exponencial en caso de errores
                const newErrorCount = retryCountRef.current + 1;
                const backoffInterval = Math.min(60000, POLLING_INTERVAL * Math.pow(2, newErrorCount));

                //console.log(`âš ï¸ LiveDataOpt - Error #${newErrorCount}. PrÃ³ximo intento en ${backoffInterval}ms`);
                const timerId = setTimeout(smartPoll, backoffInterval);
                intervalRef.current = timerId;
                activeTimers.push(timerId); // Registrar en array global
            }
        };

        // Iniciar primer poll inmediatamente
        //console.log(`ðŸš€ [${instanceIdRef.current}] LiveDataOpt - Iniciando sistema de polling inteligente`);
        smartPoll();
    }, [fetchLiveData]);

    // ðŸ”„ SETUP POLLING AL MONTAR Y CUANDO CAMBIA RIG
    useEffect(() => {
        //console.log(`ðŸ”„ [${instanceIdRef.current}] LiveDataOpt - useEffect rig cambiÃ³ a: ${rig}`);

        // Limpiar recursos previos
        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
        }

        if (displayTimerRef.current) {
            clearTimeout(displayTimerRef.current);
            displayTimerRef.current = null;
        }

        // Reset bandera de polling
        isPollingActiveRef.current = false;

        // Reset estados y datos al cambiar rig
        nbRef.current = null;
        pendingUpdatesRef.current = [];
        enVivoRef.current = false;
        setEnVivo(false);
        setLiveDataM([]); // Limpiar datos del rig anterior
        setLastUpdateDate(null);

        if (!rig || rig === 0 || rig === '') {
            return;
        }

        // Iniciar polling inteligente
        startSmartPolling();

        // Cleanup
        return () => {
            const myInstanceId = instanceIdRef.current;
            //console.log(`ðŸ§¹ [${myInstanceId}] LiveDataOpt - Cleanup de useEffect`);

            // Cancelar timer de esta instancia
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
                // Remover del array global
                activeTimers = activeTimers.filter(t => t !== intervalRef.current);
                intervalRef.current = null;
            }

            if (displayTimerRef.current) {
                clearTimeout(displayTimerRef.current);
                displayTimerRef.current = null;
            }

            // Liberar instancia global solo si somos la instancia activa
            if (activePollingInstance === myInstanceId) {
                //console.log(`ðŸ”“ [${myInstanceId}] LiveDataOpt - Liberando instancia global y cancelando todos los timers`);
                // Cancelar TODOS los timers cuando se desmonta la instancia activa
                activeTimers.forEach(timer => clearTimeout(timer));
                activeTimers = [];
                activePollingInstance = null;
            }

            isPollingActiveRef.current = false;
            pendingUpdatesRef.current = [];
        };
    }, [rig]); // Solo rig, NO startSmartPolling para evitar re-ejecuciones

    // ðŸ‘ï¸ DETECTAR CUANDO EL USUARIO REGRESA A LA PESTAÃ‘A
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                //console.log('ðŸ‘ï¸ LiveDataOpt - Usuario regresÃ³ a la pestaÃ±a');
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []); // Sin dependencias para evitar reinicializaciones

    // ðŸ“ MANEJAR SELECCIÃ“N DE VARIABLE (abre modal en tab variable)
    const handleOpenVarSelector = useCallback((matrId) => {
        if (sinDatos) {
            showNotification('No se ha asignado una plantilla para poder editar las variables', 'error');
            setShowModalPlantilla(true);
            return;
        }
        setUpdateVarMatrizId(matrId);
        setModalInitialTab('variable');
        setShowModalConfig(true);
    }, [sinDatos]);

    // ðŸš¨ MANEJAR ALARMA (abre modal en tab alarma)
    const handleOpenAlarm = useCallback((ld) => {

        setCurrentAlarm({
            varName: ld.vr_var_nom,
            varId: ld.nb_var_idn,
            min: ld.nb_alarm_min,
            max: ld.nb_alarm_max,
            exists: ld.nb_alarm_min !== null,
            id: ld.nb_alarm_idn
        });
        setUpdateVarMatrizId(ld.nb_matr_idn);
        setModalInitialTab('alarm');
        setShowModalConfig(true);
    }, []);

    // ðŸ—‘ï¸ ELIMINAR VARIABLE
    const handleDeleteVariable = useCallback(async (matrId) => {
        showConfirmDialog(
            'Â¿EstÃ¡ seguro de eliminar esta variable?',
            async () => {
                try {
                    // Actualizar UI inmediatamente (optimistic update)
                    setLiveDataM(prev => prev.filter(item => item.nb_matr_idn !== matrId));

                    // Eliminar en BD
                    await deleteBd(matrId, 'deleteVarMatriz');

                    // Reset nbRef para forzar recarga completa en el prÃ³ximo polling
                    nbRef.current = null;

                    showNotification('Variable eliminada correctamente', 'success');
                } catch (error) {
                    console.error('Error deleting variable:', error);
                    showNotification('Error al eliminar variable', 'error');
                    // Revertir cambio optimista si falla
                    await fetchLiveData();
                }
            }
        );
    }, [fetchLiveData]);

    // ðŸ”„ RECARGAR DESPUÃ‰S DE ACTUALIZAR ALARMA
    const handleAlarmUpdate = useCallback(async () => {
        // Reset nbRef para forzar recarga completa de configuraciÃ³n
        nbRef.current = null;
        await fetchLiveData();
    }, [fetchLiveData]);

    // ðŸ“‹ RECARGAR DESPUÃ‰S DE APLICAR PLANTILLA
    const handlePlantillaAplicada = useCallback(async () => {
        //console.log('ðŸ“‹ LiveDataOpt - Recargando despuÃ©s de aplicar plantilla');
        
        // Cancelar polling actual
        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
        }
        
        // Reset completo de estados
        nbRef.current = null;
        setLiveDataM([]);
        setLastUpdateDate(null);
        setSinDatos(false);
        setIsLoading(true);
        
        try {
            // Esperar un momento para que la BD procese
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Forzar recarga completa
            //console.log('ðŸ“‹ LiveDataOpt - Ejecutando fetchLiveData...');
            await fetchLiveData();
            //console.log('ðŸ“‹ LiveDataOpt - fetchLiveData completado');
            
            // Reiniciar polling
            if (!intervalRef.current) {
                //console.log('ðŸ“‹ LiveDataOpt - Reiniciando polling...');
                startSmartPolling();
            }
        } catch (error) {
            //console.error('âŒ Error recargando despuÃ©s de plantilla:', error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchLiveData, startSmartPolling]);

    // ðŸŽ¯ DRAG AND DROP - Intercambiar posiciones
    const handleDragStart = useCallback((e, item) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    }, []);

    const handleDragEnd = useCallback((e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedItem(null);
        setDragOverItem(null);
    }, []);

    const handleDragOver = useCallback((e, item) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverItem(item);
    }, []);

    const handleDrop = useCallback(async (e, targetItem) => {
        e.preventDefault();

        if (!draggedItem || draggedItem.nb_matr_idn === targetItem.nb_matr_idn) {
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        try {
            const fecha = new Date();

            // Intercambiar los nb_var_idn entre los dos registros
            const draggedVarId = draggedItem.nb_var_idn;
            const targetVarId = targetItem.nb_var_idn;

            // Actualizar nb_var_idn del elemento arrastrado con el del destino
            await updateTableBd('updatevarmatriz', {
                nb_matr_idn: parseInt(draggedItem.nb_matr_idn),
                nb_var_idn: parseInt(targetVarId),
                vr_adt_usureg: user,
                dt_adt_fchreg: fecha.toISOString()
            });

            // Actualizar nb_var_idn del elemento destino con el del arrastrado
            await updateTableBd('updatevarmatriz', {
                nb_matr_idn: parseInt(targetItem.nb_matr_idn),
                nb_var_idn: parseInt(draggedVarId),
                vr_adt_usureg: user,
                dt_adt_fchreg: fecha.toISOString()
            });

            showNotification('Variables intercambiadas correctamente', 'success');

            // Recargar datos
            nbRef.current = null;
            await fetchLiveData();
        } catch (error) {
            console.error('Error intercambiando variables:', error);
            showNotification('Error al intercambiar variables', 'error');
        }

        setDraggedItem(null);
        setDragOverItem(null);
    }, [draggedItem, user, fetchLiveData]);

    // ðŸŽ¨ CALCULAR SI VALOR ESTÃ EN ALARMA
    const isInAlarmRange = useCallback((value, min, max) => {
        if (min === null || max === null) return true;
        const val = parseFloat(value);
        const minVal = parseFloat(min);
        const maxVal = parseFloat(max);
        return val >= minVal && val <= maxVal;
    }, []);

    // ðŸ”¢ FORMATEAR VALOR
    const formatValue = useCallback((value, unit) => {
        if (unit === 'no/yes') {
            return value === '0' ? 'NO' : 'YES';
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';

        const decimal = parseFloat(numValue - parseInt(numValue)).toFixed(2);
        return parseFloat(decimal) > 0 ? numValue.toFixed(2) : numValue.toFixed(0);
    }, []);

    // ðŸ“Š CALCULAR MENSAJE DE ESTADO
    const statusMessage = useMemo(() => {
        if (!lastUpdateDate) {
            return { text: 'Sin datos transmitidos', isLive: false };
        }

        const fecha = moment.utc(lastUpdateDate).format('MMMM Do YYYY, h:mm:ss a');

        return {
            text: fecha,
            isLive: enVivo
        };
    }, [lastUpdateDate, enVivo]);

    // ðŸŽ´ RENDERIZAR TARJETA PRINCIPAL (Vista Large)
    const renderPrimaryCard = useCallback((ld, index) => {
        const inAlarm = isInAlarmRange(ld.nb_mda_val, ld.nb_alarm_min, ld.nb_alarm_max);
        const formattedValue = formatValue(ld.nb_mda_val, ld.vr_var_umd);
        const isLastItem = parseInt(ld.nb_matr_colum) === liveDataM.length - 1;
        const isDragging = draggedItem?.nb_matr_idn === ld.nb_matr_idn;
        const isDragOver = dragOverItem?.nb_matr_idn === ld.nb_matr_idn;

        return (
            <div
                className={`ldopt-card ldopt-card-primary ${isDragging ? 'ldopt-dragging' : ''} ${isDragOver ? 'ldopt-drag-over' : ''}`}
                key={`${ld.nb_matr_colum}-${ld.nb_matr_idn}`}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, ld)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, ld)}
                onDrop={(e) => handleDrop(e, ld)}
                style={{ backgroundColor: theme.cardBg, borderColor: theme.cardBorder, color: theme.fontColor }}
            >
                <div className="ldopt-card-header">
                    <h3
                        className="ldopt-var-name"
                        onClick={() => handleOpenVarSelector(ld.nb_matr_idn)}
                        title="Click para cambiar variable"
                    >
                        {ld.vr_var_nom}
                    </h3>
                    {isLastItem && (
                        <button
                            className="ldopt-delete-btn"
                            onClick={() => handleDeleteVariable(ld.nb_matr_idn)}
                            title="Eliminar variable"
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>

                <h2
                    className={`ldopt-value ldopt-value-large ${inAlarm ? 'ldopt-value-normal' : 'ldopt-value-alarm'}`}
                    onClick={() => handleOpenAlarm(ld)}
                    title="Click para configurar alarma"
                    style={{ color: inAlarm ? theme.measureValueColor : theme.alarmColor }}
                >
                    {formattedValue}
                </h2>

                <p className="ldopt-unit">
                    {ld.vr_var_umd}
                    {ld.nb_alarm_min !== null && (
                        <span className="ldopt-alarm-indicator" title={`Alarma: ${ld.nb_alarm_min} - ${ld.nb_alarm_max}`}>
                            ðŸš¨
                        </span>
                    )}
                </p>
            </div>
        );
    }, [liveDataM.length, isInAlarmRange, formatValue, handleOpenVarSelector, handleDeleteVariable, handleOpenAlarm, draggedItem, dragOverItem, handleDragStart, handleDragEnd, handleDragOver, handleDrop, theme]);

    // ðŸŽ´ RENDERIZAR TARJETA SECUNDARIA (Vista Normal)
    const renderSecondaryCard = useCallback((ld, index) => {
        const inAlarm = isInAlarmRange(ld.nb_mda_val, ld.nb_alarm_min, ld.nb_alarm_max);
        const formattedValue = formatValue(ld.nb_mda_val, ld.vr_var_umd);
        const isLastItem = parseInt(ld.nb_matr_colum) === liveDataM.length - 1;
        const isDragging = draggedItem?.nb_matr_idn === ld.nb_matr_idn;
        const isDragOver = dragOverItem?.nb_matr_idn === ld.nb_matr_idn;

        return (
            <div
                className={`ldopt-card ldopt-card-secondary ${isDragging ? 'ldopt-dragging' : ''} ${isDragOver ? 'ldopt-drag-over' : ''}`}
                key={`${ld.nb_matr_colum}-${ld.nb_matr_idn}`}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, ld)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, ld)}
                onDrop={(e) => handleDrop(e, ld)}
                style={{ backgroundColor: theme.cardBg, borderColor: theme.cardBorder, color: theme.fontColor }}
            >
                <div className="ldopt-card-header">
                    <h6
                        className="ldopt-var-name-small"
                        onClick={() => handleOpenVarSelector(ld.nb_matr_idn)}
                        title="Click para cambiar variable"
                    >
                        {ld.vr_var_nom}
                    </h6>
                    {isLastItem && !isMobilePortrait && (
                        <button
                            className="ldopt-delete-btn-small"
                            onClick={() => handleDeleteVariable(ld.nb_matr_idn)}
                            title="Eliminar variable"
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>

                <h3
                    className={`ldopt-value ldopt-value-small ${inAlarm ? 'ldopt-value-normal' : 'ldopt-value-alarm'}`}
                    onClick={() => handleOpenAlarm(ld)}
                    title="Click para configurar alarma"
                    style={{ color: inAlarm ? theme.measureValueColor : theme.alarmColor }}
                >
                    {formattedValue}
                </h3>

                <p className="ldopt-unit-small">
                    {ld.vr_var_umd}
                    {ld.nb_alarm_min !== null && (
                        <span className="ldopt-alarm-indicator ldopt-alarm-indicator-small" title={`Alarma: ${ld.nb_alarm_min} - ${ld.nb_alarm_max}`}>
                            ðŸš¨
                        </span>
                    )}
                </p>
            </div>
        );
    }, [liveDataM.length, isMobilePortrait, isInAlarmRange, formatValue, handleOpenVarSelector, handleDeleteVariable, handleOpenAlarm, draggedItem, dragOverItem, handleDragStart, handleDragEnd, handleDragOver, handleDrop, theme]);

    // ðŸ“ CALCULAR CLASE DE GRID ITEM
    const getGridItemClass = useCallback((isAddButton = false) => {
        if (tipoVista === 'large') {
            return 'ldopt-grid-item ldopt-grid-item-large';
        }
        if (isMobileLandscape) {
            return 'ldopt-grid-item ldopt-grid-item-mobile-landscape';
        }
        if (isMobilePortrait) {
            return 'ldopt-grid-item ldopt-grid-item-mobile-portrait';
        }
        return 'ldopt-grid-item ldopt-grid-item-desktop';
    }, [tipoVista, isMobileLandscape, isMobilePortrait]);

    // âž• RENDERIZAR BOTÃ“N AGREGAR
    const renderAddButton = useMemo(() => {
        if (sinDatos) return null;

        return (
            <div className={getGridItemClass(true)}>
                <div className="ldopt-card ldopt-card-add">
                    <h6 className="ldopt-add-title">Seleccionar</h6>
                    <button
                        className="ldopt-add-btn"
                        onClick={() => handleOpenVarSelector(0)}
                        title="Agregar variable"
                    >
                        <i className="fa-solid fa-plus"></i>
                    </button>
                </div>
            </div>
        );
    }, [sinDatos, getGridItemClass, handleOpenVarSelector]);

    // ðŸŽ¨ RENDERIZAR CONTENIDO PRINCIPAL
    return (
        <div
            className={`ldopt-container ${isMobilePortrait ? 'ldopt-mobile-portrait' : ''} ${isMobileLandscape ? 'ldopt-mobile-landscape' : ''}`}
            key={forceRenderKey}
            style={{ backgroundColor: theme.bgColor, color: theme.fontColor }}
        >
            {/* 📊 HEADER CON ESTADO Y CONTROLES */}
            <div className="ldopt-header">
                {rig !== 0 ? (
                    <>
                        <div className="ldopt-status">
                            <span className="ldopt-status-text">{statusMessage.text}</span>
                            <i
                                className="fa-solid fa-circle ldopt-status-icon"
                                style={{ color: statusMessage.isLive ? 'green' : 'red' }}
                            />
                        </div>

                        <div className="ldopt-controls">
                            <button
                                className="ldopt-control-btn"
                                onClick={() => setShowModalPlantilla(true)}
                                title="Gestionar plantilla"
                            >
                                <i className="fa-regular fa-object-group"></i>
                            </button>

                            {sinDatos && (
                                <button
                                    className="ldopt-control-btn ldopt-warning-btn"
                                    onClick={() => showNotification('Recuerde asignar una plantilla para poder modificar el orden y las variables', 'warning')}
                                    title="Plantilla no asignada"
                                >
                                    <i className="fa-solid fa-exclamation-triangle"></i>
                                </button>
                            )}

                            <button
                                className="ldopt-control-btn"
                                onClick={() => setTipoVista(tipoVista === 'live' ? 'large' : 'live')}
                                title={tipoVista === 'live' ? 'Vista grande' : 'Vista compacta'}
                            >
                                <i className={`fa fa-${tipoVista === 'live' ? 'table' : 'braille'}`}></i>
                            </button>
                        </div>
                    </>
                ) : (
                    <h5 className="ldopt-empty-message">Seleccione un Rig para visualizar</h5>
                )}
            </div>

            {/* ðŸ”„ LOADER */}
            {isLoading && (
                <div className="ldopt-loader">
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span>Cargando datos...</span>
                </div>
            )}

            {/* ðŸ“Š GRID DE VARIABLES */}
            {!isLoading && (
                <div className={`ldopt-grid ${tipoVista === 'large' ? 'ldopt-grid-large' : 'ldopt-grid-normal'} ${isMobileLandscape ? 'ldopt-grid-mobile-landscape' : ''} ${isMobilePortrait ? 'ldopt-grid-mobile-portrait' : ''}`}>
                    {liveDataM.map((ld, index) => {
                        const showPrimary = tipoVista === 'large' && index < 8;
                        return (
                            <div className={getGridItemClass()} key={`${ld.nb_matr_colum}-${ld.nb_matr_idn}`}>
                                {showPrimary ? renderPrimaryCard(ld, index) : renderSecondaryCard(ld, index)}
                            </div>
                        );
                    })}
                    {renderAddButton}
                </div>
            )}

            {/* ðŸ“ MODAL UNIFICADO */}
            <ModalVariableConfigOpt
                show={showModalConfig}
                handleClose={() => setShowModalConfig(false)}
                rig={rig}
                currentVariable={{
                    vr_var_nom: currentAlarm.varName,
                    nb_var_idn: currentAlarm.varId,
                    nb_alarm_min: currentAlarm.min,
                    nb_alarm_max: currentAlarm.max,
                    nb_alarm_idn: currentAlarm.id
                }}
                variables={variables}
                updateVarMatrizId={updateVarMatrizId}
                uid={uid}
                user={user}
                onUpdate={handleAlarmUpdate}
                initialTab={modalInitialTab}
                theme={theme}
            />

            {showModalPlantilla && (
                <ModalPlantillaOpt
                    show={showModalPlantilla}
                    handleClose={() => setShowModalPlantilla(false)}
                    rig={rig}
                    liveDataM={liveDataM}
                    setIsLoading={setIsLoading}
                    onUpdate={handlePlantillaAplicada}
                    tipo="live"
                    uid={uid}
                    user={user}
                    theme={theme}
                />
            )}

            {/* ðŸ“¢ COMPONENTE DE NOTIFICACIONES */}
            <NotificationFooter />

            {/* â“ MODAL DE CONFIRMACIÃ“N */}
            <ConfirmDialog />
        </div>
    );
};

export default React.memo(LiveDataOpt);
